import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiError } from '../../lib/api';
import { getModerationDetail } from '../../lib/moderationApi';
import { ModerationReviewActions } from './ModerationReviewActions';
import {
  ErrorMessage,
  Section,
  StatusBadge,
  secondaryButtonClass,
} from './ModerationUi';

const formatEventTime = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainder = seconds % 60;
  return [hours, minutes, remainder].map((part) => String(part).padStart(2, '0')).join(':');
};

const reviewDecisionLabels = {
  APPROVE: 'Одобрено',
  REJECT: 'Отказано',
  CONFIRM: 'Подтверждено',
  DISPUTE: 'Оспорено',
} as const;

export function ModerationDetailPage({
  publicId,
  onNavigate,
  onAuthorizationFailure,
}: {
  publicId: string;
  onNavigate: (path: string) => void;
  onAuthorizationFailure: () => void;
}): JSX.Element {
  const detail = useQuery({
    queryKey: ['moderation', 'detail', publicId],
    queryFn: () => getModerationDetail(publicId),
  });

  useEffect(() => {
    if (
      detail.error instanceof ApiError &&
      (detail.error.status === 401 || detail.error.status === 403)
    ) {
      onAuthorizationFailure();
    }
  }, [detail.error, onAuthorizationFailure]);

  if (detail.isLoading) {
    return <main role="status" className="mx-auto max-w-5xl p-8 text-center">Загрузка заявки…</main>;
  }
  if (detail.error || !detail.data) {
    return (
      <main className="mx-auto max-w-5xl space-y-4 p-6">
        <ErrorMessage error={detail.error} />
        <button className={secondaryButtonClass} type="button" onClick={() => void detail.refetch()}>
          Повторить
        </button>
        <button className={secondaryButtonClass} onClick={() => onNavigate('/moderation/manual-corrections')}>
          Вернуться в очередь
        </button>
      </main>
    );
  }

  const item = detail.data;
  return (
    <main className="mx-auto w-full max-w-5xl space-y-5 px-4 py-6 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <button className={secondaryButtonClass} onClick={() => onNavigate('/moderation/manual-corrections')}>
            ← Очередь
          </button>
          <h1 className="mt-3 text-2xl font-semibold">Заявка {item.public_id}</h1>
        </div>
        <StatusBadge status={item.status} />
      </div>

      <Section title="Исходное заявление">
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-muted-foreground">Реплей</dt>
            <dd>
              <a
                className="break-all text-primary underline underline-offset-2"
                href={item.replay_url}
                target="_blank"
                rel="noopener noreferrer"
                referrerPolicy="no-referrer"
              >
                Ссылка (откроется в новой вкладке)
              </a>
            </dd>
          </div>
          <div><dt className="text-muted-foreground">Тайминг события</dt><dd>{formatEventTime(item.event_time_seconds)}</dd></div>
          <div>
            <dt className="text-muted-foreground">Заявленный автор фрага</dt>
            <dd>
              {item.claimed_killer_name}
              {item.claimed_killer_squad ? ` [${item.claimed_killer_squad}]` : null}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Заявленная жертва</dt>
            <dd>
              {item.claimed_victim_name}
              {item.claimed_victim_squad ? ` [${item.claimed_victim_squad}]` : null}
            </dd>
          </div>
          <div><dt className="text-muted-foreground">Оружие</dt><dd>{item.claimed_weapon_or_method || 'не указано'}</dd></div>
          <div><dt className="text-muted-foreground">Дистанция</dt><dd>{item.claimed_distance ?? 'не указана'}</dd></div>
        </dl>
        <p className="mt-4 whitespace-pre-wrap rounded-md bg-muted/40 p-3 text-sm">
          {item.description || 'Описание обстоятельств не указано.'}
        </p>
      </Section>

      <Section title="Доказательства">
        <p className="mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
          Это пользовательские внешние ссылки. Перед переходом проверьте показанный адрес;
          не вводите на открывшемся сайте пароль от Webstat.
        </p>
        {item.evidence.length ? (
          <ul className="space-y-2">
            {item.evidence.map((evidence) => (
              <li key={evidence.url} className="rounded-md border border-border p-3">
                <a
                  className="break-all text-primary underline underline-offset-2"
                  href={evidence.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  referrerPolicy="no-referrer"
                >
                  {evidence.label || evidence.url}
                </a>
                {evidence.label ? (
                  <p className="mt-1 break-all text-xs text-muted-foreground">
                    {evidence.url}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        ) : <p className="text-sm text-muted-foreground">Ссылки не приложены.</p>}
      </Section>

      {item.proposal ? (
        <Section title="Зафиксированное предложение корректировки">
          <dl className="grid gap-2 text-sm sm:grid-cols-3">
            <div><dt className="text-muted-foreground">Событие</dt><dd>{item.proposal.k_name} → {item.proposal.v_name}</dd></div>
            <div><dt className="text-muted-foreground">Оружие</dt><dd>{item.proposal.w_class} / {item.proposal.type_w}</dd></div>
            <div>
              <dt className="text-muted-foreground">Флаги</dt>
              <dd className="space-y-1">
                <span className="block">tk - Team kill: {item.proposal.tk}</span>
                <span className="block">wk - Vehicle frag: {item.proposal.wk}</span>
                <span className="block">ak - Artillery frag: {item.proposal.ak}</span>
              </dd>
            </div>
            <div className="sm:col-span-3"><dt className="text-muted-foreground">Hash</dt><dd className="break-all font-mono text-xs">{item.proposal.payload_hash}</dd></div>
          </dl>
        </Section>
      ) : null}

      <Section title="История рассмотрения">
        {item.reviews.length ? (
          <ol className="space-y-3">
            {item.reviews.map((review) => (
              <li key={review.stage} className="rounded-md border border-border p-3 text-sm">
                <p className="font-semibold">
                  {review.reviewer_label}: {reviewDecisionLabels[review.decision]}
                </p>
                <p className="mt-1 whitespace-pre-wrap">{review.comment}</p>
                {review.public_rejection_reason_snapshot ? (
                  <p className="mt-2 text-muted-foreground">
                    Публичная причина: {review.public_rejection_reason_snapshot}
                  </p>
                ) : null}
              </li>
            ))}
          </ol>
        ) : <p className="text-sm text-muted-foreground">Решений пока нет.</p>}
      </Section>

      {item.published_manual_entry_id ? (
        <div role="status" className="rounded-lg border border-primary/40 bg-primary/10 p-4">
          Создана manual row #{item.published_manual_entry_id}. Заявка ожидает штатного пересчёта.
        </div>
      ) : null}

      <Section title="Действие">
        <ModerationReviewActions detail={item} />
      </Section>
    </main>
  );
}
